import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Route, Routes, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { seedCatalogIfEmpty } from "@/lib/catalog/catalogSeed";
import { seedBrandsIfEmpty } from "@/lib/catalog/brandsStore";
import { seedEmployeeAllowanceTypesIfEmpty } from "@/lib/hr/employeeAllowanceTypesStore";
import { seedEmployeeDeductionTypesIfEmpty } from "@/lib/hr/employeeDeductionTypesStore";
import { seedDepartmentsIfEmpty } from "@/lib/hr/departmentsStore";
import { seedDesignationsIfEmpty } from "@/lib/hr/designationsStore";
import { getCurrentUser } from "@/services/authService";
import { normalizeAuthUser } from "@/lib/authUser";
import { applyPrimaryColorToDom } from "@/lib/theme";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import AddProduct from "./pages/AddProduct";
import ProductView from "./pages/ProductView";
import Customers from "./pages/Customers";
import Suppliers from "./pages/Suppliers";
import Investors from "./pages/Investors";
import Orders from "./pages/Orders";
import Invoices from "./pages/Invoices";
import AccidentalCars from "./pages/AccidentalCars";
import CustomDuty from "./pages/CustomDuty";
import Categories from "./pages/Categories";
import AttributeGroups from "./pages/AttributeGroups";
import Attributes from "./pages/Attributes";
import Warehouses from "./pages/Warehouses";
import Brands from "./pages/Brands";
import VehicleModels from "./pages/VehicleModels";
import VehicleVersions from "./pages/VehicleVersions";
import PurchaseCostCategories from "./pages/PurchaseCostCategories";
import PurchaseCostTypes from "./pages/PurchaseCostTypes";
import Departments from "./pages/Departments";
import Designations from "./pages/Designations";
import EmployeeAllowanceTypes from "./pages/EmployeeAllowanceTypes";
import EmployeeDeductionTypes from "./pages/EmployeeDeductionTypes";
import SalaryBatches from "./pages/SalaryBatches";
import SalaryIncrements from "./pages/SalaryIncrements";
import EmployeeLoans from "./pages/EmployeeLoans";
import SalaryAdvances from "./pages/SalaryAdvances";
import SalaryAdjustments from "./pages/SalaryAdjustments";
import SalaryPayments from "./pages/SalaryPayments";
import PayrollAttendances from "./pages/PayrollAttendances";
import LeaveTypes from "./pages/LeaveTypes";
import LeavePolicies from "./pages/LeavePolicies";
import LeaveRequests from "./pages/LeaveRequests";
import LeaveYears from "./pages/LeaveYears";
import WorkPolicies from "./pages/WorkPolicies";
import SalaryPolicies from "./pages/SalaryPolicies";
import WorkShifts from "./pages/WorkShifts";
import Banks from "./pages/Banks";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import Currencies from "./pages/Currencies";
import Rosters from "./pages/Rosters";
import WorkingCalendar from "./pages/WorkingCalendar";
import AttendanceDaily from "./pages/AttendanceDaily";
import AttendanceRules from "./pages/AttendanceRules";
import AttendancePunches from "./pages/AttendancePunches";
import AttendanceAdjustments from "./pages/AttendanceAdjustments";
import ExpenseCategories from "./pages/ExpenseCategories";
import Expenses from "./pages/Expenses";
import Roles from "./pages/Roles";
import PermissionCategories from "./pages/PermissionCategories";
import Permissions from "./pages/Permissions";
import AddCompany from "./pages/AddCompany";
import CompanyView from "./pages/CompanyView";
import Companies from "./pages/Companies";
import EditCompany from "./pages/EditCompany";
import BiometricDevices from "./pages/BiometricDevices";
import AddBiometricDevice from "./pages/AddBiometricDevice";
import EditBiometricDevice from "./pages/EditBiometricDevice";
import BiometricDeviceView from "./pages/BiometricDeviceView";
import CompanyBiometricDevices from "./pages/CompanyBiometricDevices";
import AddCompanyBiometricDevice from "./pages/AddCompanyBiometricDevice";
import EditCompanyBiometricDevice from "./pages/EditCompanyBiometricDevice";
import CompanyBiometricDeviceView from "./pages/CompanyBiometricDeviceView";
import Employees from "./pages/Employees";
import AddEmployee from "./pages/AddEmployee";
import EmployeeView from "./pages/EmployeeView";
import Sales from "./pages/Sales";
import CreateSale from "./pages/CreateSale";
import SaleInvoice from "./pages/SaleInvoice";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

function SalaryBatchLegacyRedirect() {
  const { batchId } = useParams();
  return <Navigate to={`/payroll/salary-batches?batch=${encodeURIComponent(batchId ?? "")}`} replace />;
}

function SalaryProcessRedirect() {
  const [searchParams] = useSearchParams();
  const batch = searchParams.get("batch");
  const target = batch ? `/payroll/salary-batches?batch=${encodeURIComponent(batch)}` : "/payroll/salary-batches";
  return <Navigate to={target} replace />;
}

const ProtectedRoute = ({
  isCheckingAuth,
  children,
}: {
  isCheckingAuth: boolean;
  children: JSX.Element;
}) => {
  const isAuthenticated = Boolean(localStorage.getItem("auth_user"));
  if (isCheckingAuth) {
    return <div className="min-h-screen bg-background" />;
  }
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const PublicOnlyRoute = ({
  isCheckingAuth,
  children,
}: {
  isCheckingAuth: boolean;
  children: JSX.Element;
}) => {
  const isAuthenticated = Boolean(localStorage.getItem("auth_user"));
  if (isCheckingAuth) {
    return <div className="min-h-screen bg-background" />;
  }
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

const App = () => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const isAuthenticated = Boolean(localStorage.getItem("auth_user"));

  useEffect(() => {
    seedCatalogIfEmpty();
    seedDepartmentsIfEmpty();
    seedDesignationsIfEmpty();
    seedEmployeeAllowanceTypesIfEmpty();
    seedEmployeeDeductionTypesIfEmpty();
    try {
      const raw = localStorage.getItem("auth_user");
      if (raw) {
        const u = JSON.parse(raw);
        applyPrimaryColorToDom(u?.company_branding?.primary_color);
      }
    } catch {}
  }, []); // intentionally run once; focus/visibility handlers refresh auth_user

  useEffect(() => {
    let mounted = true;

    const bootstrapAuth = async () => {
      try {
        const response = await getCurrentUser();
        const user = normalizeAuthUser(response);
        if (!mounted) {
          return;
        }
        if (user) {
          localStorage.setItem("auth_user", JSON.stringify(user));
          applyPrimaryColorToDom(user.company_branding?.primary_color);
        } else {
          localStorage.removeItem("auth_user");
          applyPrimaryColorToDom(null);
        }
      } catch {
        if (mounted) {
          localStorage.removeItem("auth_user");
          applyPrimaryColorToDom(null);
        }
      } finally {
        if (mounted) {
          setIsCheckingAuth(false);
        }
      }
    };

    bootstrapAuth();

    const onFocus = () => {
      // Keep permissions fresh without hard refresh (Ctrl+Shift+R).
      void bootstrapAuth();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void bootstrapAuth();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mounted = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <TooltipProvider delayDuration={250} skipDelayDuration={100}>
        <Toaster />
        <Sonner position="top-right" />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />
            <Route
              path="/login"
              element={
                <PublicOnlyRoute isCheckingAuth={isCheckingAuth}>
                  <Login />
                </PublicOnlyRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicOnlyRoute isCheckingAuth={isCheckingAuth}>
                  <Register />
                </PublicOnlyRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Products />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/add"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AddProduct />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/:id"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <ProductView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/:id/edit"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AddProduct />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Categories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attribute-groups"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AttributeGroups />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attributes"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Attributes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ware-houses"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Warehouses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brands"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Brands />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vehicle-models"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <VehicleModels />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vehicle-versions"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <VehicleVersions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase-cost-categories"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <PurchaseCostCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase-cost-types"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <PurchaseCostTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Customers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Suppliers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/investors"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Investors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Invoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accidental-cars"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AccidentalCars />
                </ProtectedRoute>
              }
            />
            <Route
              path="/custom-duty"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <CustomDuty />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-sales/new"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <CreateSale />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-sales/:id/edit"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <CreateSale />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-sales"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Sales />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-sales/:id"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <SaleInvoice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Employees />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees/add"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AddEmployee />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees/:id"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <EmployeeView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees/:id/edit"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AddEmployee />
                </ProtectedRoute>
              }
            />
            <Route
              path="/departments"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Departments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/designations"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Designations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-allowance-types"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <EmployeeAllowanceTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-deduction-types"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <EmployeeDeductionTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/salary-process"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <SalaryProcessRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/salary-batches"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <SalaryBatches />
                </ProtectedRoute>
              }
            />
            <Route path="/payroll/salary-process/batches/:batchId" element={<SalaryBatchLegacyRedirect />} />
            <Route
              path="/payroll/attendances"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <PayrollAttendances />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/salary-increments"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <SalaryIncrements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/loans"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <EmployeeLoans />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/salary-advances"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <SalaryAdvances />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/salary-adjustments"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <SalaryAdjustments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/salary-payments"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <SalaryPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-types"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <LeaveTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-policies"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <LeavePolicies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-years"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <LeaveYears />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-requests"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <LeaveRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-policies"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <WorkPolicies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/salary-policies"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <SalaryPolicies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-shifts"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <WorkShifts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/banks"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Banks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/currencies"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Currencies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/chart-of-accounts"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <ChartOfAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rosters"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Rosters />
                </ProtectedRoute>
              }
            />
            <Route
              path="/working-calendar"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <WorkingCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Navigate to="/attendance/daily" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance/daily"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AttendanceDaily />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance/rules"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AttendanceRules />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance/punches"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AttendancePunches />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance/adjustments"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AttendanceAdjustments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expense-categories"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <ExpenseCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Roles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/permission-categories"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <PermissionCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/permissions"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Permissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-access"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Navigate to="/companies/add" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-access/add"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Navigate to="/companies/add" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies/add"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AddCompany />
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies/:id/edit"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <EditCompany />
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies/:id"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <CompanyView />
                </ProtectedRoute>
              }
            />
            <Route path="/companies/all" element={<Navigate to="/companies" replace />} />
            <Route
              path="/companies"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Companies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/biometric-devices/add"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AddBiometricDevice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/biometric-devices/:id/edit"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <EditBiometricDevice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/biometric-devices/:id"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <BiometricDeviceView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/biometric-devices"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <BiometricDevices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company-biometric-devices/add"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <AddCompanyBiometricDevice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company-biometric-devices/:id/edit"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <EditCompanyBiometricDevice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company-biometric-devices/:id"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <CompanyBiometricDeviceView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company-biometric-devices"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <CompanyBiometricDevices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <ProtectedRoute isCheckingAuth={isCheckingAuth}>
                  <Expenses />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  );
};

export default App;
